import TabbedContentSlide from './TabbedContentSlide';
import Debouncer from '../../lib/Debouncer';

interface TabbedContentConfig {
	className: string;
	doClose?: boolean|((fn?: any) => boolean);
	doHeightChange?: boolean|((fn?: any) => boolean);
	doScrollToTop?: boolean|((fn?: any) => boolean);
	onLoad?: <T>(t: T) => void;
	onChange?: <T>(t: T) => void;
}

export default class TabbedContent {
	public initialized = false;
	private className: string;
	public el: Element;
	private frameEl: Element;

	private slides: TabbedContentSlide[];
	private currentIndex: number;
	private lastIndex: number;

	private onChange = (fn?: any) => fn;
	private onLoad = (fn?: any) => fn;

	private doClose: (fn?: any) => boolean;
	private doHeightChange: (fn?: any) => boolean;
	private doScrollToTop: (fn?: any) => boolean;

	private get previousSlide(): TabbedContentSlide {
		return (this.lastIndex !== undefined)
			? this.slides[this.lastIndex]
			: undefined;
	}

	public get activeSlide(): TabbedContentSlide {
		// Should only ever be one
		return this.slides[this.currentIndex];
	}

	private get previousIndex(): number {
		return this.currentIndex
			? this.currentIndex - 1
			: this.slides.length - 1;
	}

	private get nextIndex(): number {
		return this.currentIndex === this.slides.length - 1
			? 0
			: this.currentIndex + 1;
	}

	constructor({ className, doHeightChange, doClose, doScrollToTop, onChange, onLoad }: TabbedContentConfig) {
		// Grab TabbedContent element
		this.className = className;
		this.doScrollToTop =
			typeof doScrollToTop === 'boolean'
				? () => doScrollToTop
				: typeof doScrollToTop === 'function'
					? doScrollToTop
					: () => false;
		this.doClose =
			typeof doClose === 'boolean'
				? () => doClose
				: typeof doClose === 'function'
					? doClose
					: () => false;
		this.doHeightChange =
			typeof doHeightChange === 'boolean'
				? () => doHeightChange
				: typeof doHeightChange === 'function'
					? doHeightChange
					: () => false;
		this.onLoad = onLoad || this.onLoad;
		this.onChange = onChange || this.onChange;

		this.el = document.getElementsByClassName(this.className)[0];
		if (!this.el)
			return;

		// Grab frame, break if not present
		this.frameEl = this.el.getElementsByClassName(`${this.className}__frame`)[0];
		if (!this.frameEl)
			return;

		// Build slides
		this.slides = this.buildSlides({containerEl: this.el});

		// Run callbacks
		this.onLoad();

		this.attachHandlers();

		this.initialized = true;
	}

	private buildSlides = ({containerEl}: {containerEl: Element}): TabbedContentSlide[] => {
		let slides: TabbedContentSlide[] = [];

		let triggerElList: NodeListOf<Element>;
		let slideTriggerElList: NodeListOf<Element>;
		let slideElList: NodeListOf<Element>;

		// Grab slides and navs, test they exist and are same length
		triggerElList = containerEl.getElementsByClassName(`${this.className}__trigger`);
		slideTriggerElList = containerEl.getElementsByClassName(`${this.className}__slideTrigger`);
		slideElList = containerEl.getElementsByClassName(`${this.className}__slide`);
		if (!slideElList.length)
			return;

		// Loop through slides, push new TabbedContentSlide
		// NOTE: This is brittle. Slide should always have
		// corresponding nav, but what if it doesn't?
		for (let i = 0; i < slideElList.length; i++) {
			const slideEl = slideElList[i];

			if (this.currentIndex === undefined && slideEl.classList.contains(`${this.className}__slide--active`)) {
				this.currentIndex = i;
			}

			const navElList: Element[] = [];
			if (triggerElList[i]) {
				navElList.push(triggerElList[i]);
			}
			if (slideTriggerElList[i]) {
				navElList.push(slideTriggerElList[i]);
			}

			slides.push(new TabbedContentSlide({
				slideEl,
				navElList,
				rootClassName: this.className,
			}));
		}

		return slides;
	}

	private frameHeightToSlide = (slide: Element, frameEl: Element): void => {
		if (this.doHeightChange()) {
			const slideHeight = slide.getBoundingClientRect().height;
			const changeHeight = () => {
				(<HTMLElement>frameEl).style.height = `${slideHeight}px`;
			};
			requestAnimationFrame(changeHeight);
		} else {
			// Necessary as some stop doing height change at mobile
			(<HTMLElement>frameEl).style.height = null;
		}

		window.dispatchEvent(new CustomEvent('heightchange'));
	}

	public frameHeightToActiveSlide = () => {
		this.frameHeightToSlide(this.activeSlide.slideEl, this.frameEl);
	}

	public activateSlideByElement = (element: Element) => {
		let newIndex = -1;
		for (let i = 0; i < this.slides.length; i++) {
			if (this.slides[i].slideEl === element) {
				newIndex = i;
				break;
			}
		}

		if (newIndex >= 0) {
			this.activateSlideByIndex(newIndex);
		}
	}
	public activateSlideByIndex = (newIndex: number) => {
		const isActiveSlide = this.currentIndex === newIndex;
		if (isActiveSlide && !this.doClose())
			return;

		// Remove previous from old previous
		if (this.previousSlide) {
			this.previousSlide.removeModifierClass('outgoing');
		}

		// Add previous to new previous and remove active
		this.lastIndex = this.currentIndex;
		if (this.previousSlide) {
			this.previousSlide.addModifierClass('outgoing');
			this.previousSlide.removeModifierClass('active');
		}

		if (isActiveSlide && this.doClose()) {
			this.activeSlide.removeModifierClass('active');
			this.currentIndex = undefined;
			(<HTMLElement>this.frameEl).style.height = null;
		} else {
			this.currentIndex = newIndex;
			this.activeSlide.addModifierClass('active');
			this.frameHeightToActiveSlide();

			if (this.doScrollToTop()) {
				this.activeSlide.slideEl.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			}
		}
		this.onChange();
	}

	private attachClickHandlerToSlide = (slide: TabbedContentSlide) => {
		if (slide.navElList.length) {
			slide.navElList.forEach(el => {
				el.addEventListener(
					'click',
					() => {
						this.activateSlideByElement(slide.slideEl);
					});
				}
			);
		}
	}

	private fontLoadedHandler = () => {
		this.frameHeightToActiveSlide();
		window.removeEventListener('fontloaded', this.fontLoadedHandler);
	}

	private attachHandlers = () => {
		// Attach to nav triggers
		this.slides.forEach(this.attachClickHandlerToSlide);

		// Attach to controls, if any
		this.el.addEventListener('click', (event: MouseEvent) => {
			const target: Element = event.target as Element;
			const prevBtn = target.closest(`.${this.className}__control--prev`) as HTMLButtonElement;
			const nextBtn = target.closest(`.${this.className}__control--next`) as HTMLButtonElement;
			const resetBtn = target.closest(`.${this.className}__control--restart`) as HTMLButtonElement;

			if (prevBtn && !prevBtn.disabled) {
				this.activateSlideByIndex(this.previousIndex);
			} else if (nextBtn && !nextBtn.disabled) {
				this.activateSlideByIndex(this.nextIndex);
			} else if (resetBtn && !resetBtn.disabled) {
				this.activateSlideByIndex(0);
			}
		});

		if (this.doHeightChange) {
			window.addEventListener('fontloaded', this.fontLoadedHandler);

			const resizeDebouncer = new Debouncer(this.frameHeightToActiveSlide);
			window.addEventListener('resize', resizeDebouncer.run, false);
		}
	}
}
